import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FiCopy } from "react-icons/fi";

import { createGraphShare, graphTargets, type GraphTargetDevice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";

type Picked = { port_id: number; label: string; hint: string };

const EXPIRY_DAYS = [30, 90, 365, 0];

/**
 * Create a customer share link. The generated URL is displayed once
 * and never again: only its hash is stored, so nothing can reprint it.
 */
export default function ShareDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(90);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState("");
  const targets = useQuery({ queryKey: ["graph-targets", ""], queryFn: () => graphTargets("") });
  const devices: GraphTargetDevice[] = targets.data?.devices ?? [];
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const device = devices.find((d) => d.device_id === deviceId);

  const create = useMutation({
    mutationFn: () =>
      createGraphShare({ label: label.trim(), days, ports: picked.map((p) => ({ port_id: p.port_id, label: p.label })) }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["graph-shares"] });
      setUrl(res.url ?? res.token ?? null);
    },
  });

  const add = (portId: number, hint: string) => {
    if (picked.some((p) => p.port_id === portId)) return;
    setPicked([...picked, { port_id: portId, label: "", hint }]);
  };

  const shown = search.trim()
    ? devices.filter((d) => d.name.toLowerCase().includes(search.trim().toLowerCase()))
    : devices;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("shares.new")}</DialogTitle>
        </DialogHeader>

        {url ? (
          <div className="space-y-3">
            <p className="text-slate-400">{t("shares.created")}</p>
            <div className="flex gap-2">
              <Input readOnly value={url} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <Button size="sm" onClick={() => { navigator.clipboard?.writeText(url); setCopied(true); }}>
                <FiCopy /> {copied ? t("users.copied") : t("users.copy")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <label className="block space-y-1">
              <span className="text-xs text-slate-400">{t("shares.label")}</span>
              <Input autoFocus value={label} placeholder="PT Maju Jaya" onChange={(e) => setLabel(e.target.value)} />
              <span className="block text-xs text-slate-500">{t("shares.labelHint")}</span>
            </label>

            <div className="space-y-2">
              <span className="text-xs text-slate-400">{t("shares.pick")}</span>
              <div className="flex flex-wrap gap-2">
                <Input className="w-48" placeholder={t("graphs.searchPlaceholder")} value={search}
                       onChange={(e) => setSearch(e.target.value)} />
                <Select value={deviceId ? String(deviceId) : ""} onValueChange={(v) => setDeviceId(Number(v))}>
                  <SelectTrigger className="w-64"><SelectValue placeholder={t("graphs.devicePlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {shown.map((d) => <SelectItem key={d.device_id} value={String(d.device_id)}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value="" onValueChange={(v) => {
                  const p = device?.ports.find((x) => String(x.port_id) === v);
                  if (p && device) add(p.port_id, `${device.name} · ${p.ifname}`);
                }} disabled={!device}>
                  <SelectTrigger className="w-56"><SelectValue placeholder={t("graphs.portPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {(device?.ports ?? []).map((p) => (
                      <SelectItem key={p.port_id} value={String(p.port_id)}>{p.ifname}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {picked.map((p, i) => (
                <div key={p.port_id} className="flex items-center gap-2 rounded-md border border-slate-800 px-3 py-2">
                  <span className="w-48 shrink-0 truncate font-mono text-xs text-slate-500">{p.hint}</span>
                  <Input
                    className="flex-1" placeholder={t("shares.graphLabel")} value={p.label}
                    onChange={(e) => setPicked(picked.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                  />
                  <Button size="sm" variant="ghost" className="text-signal-crit"
                          onClick={() => setPicked(picked.filter((_, j) => j !== i))}>
                    ×
                  </Button>
                </div>
              ))}
              <p className="text-xs text-slate-500">{t("shares.customerNote")}</p>
            </div>

            <label className="block space-y-1">
              <span className="text-xs text-slate-400">{t("shares.expiry")}</span>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPIRY_DAYS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d === 0 ? t("shares.never") : t("shares.expiryDays", { n: d })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            {picked.length > 0 && (
              <div className="rounded-sm border border-signal/25 bg-signal/[0.04] px-3 py-2 text-[12.5px]">
                <p className="eyebrow mb-1">{t("shares.preview")}</p>
                <p className="text-slate-300">{t("shares.previewBody", { name: label.trim() || "—" })}</p>
                <ul className="data mt-1 space-y-0.5 text-slate-100">
                  {picked.map((p) => <li key={p.port_id}>{p.label.trim() || p.hint}</li>)}
                </ul>
                <p className="mt-1.5 text-slate-500">
                  {days === 0 ? t("shares.previewNoExpiry") : t("shares.previewExpiry", { n: days })}
                </p>
              </div>
            )}
            {create.error && <p className="text-sm text-signal-crit">{String(create.error)}</p>}
          </div>
        )}

        <DialogFooter>
          {url ? (
            <Button onClick={onClose}>{t("common.close")}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
              <Button disabled={!label.trim() || picked.length === 0 || create.isPending} onClick={() => create.mutate()}>
                {t("shares.create")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
